import React, { useState, useEffect, useCallback } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { Tables, Spinner } from "../../../../../Components";
import {
  returnPropsByPermissions,
  showError,
  showSuccess,
} from "../../../../../Helper";
import {
  GetAgreementDetailsByIdAPI,
  DeleteAgreementDetailsAPI,
} from "../../../../../Services";
import { TableActions } from "../../../../../Enums";
import { MessageConfirmDialog } from "../../../../../SharedComponents";
import { AgreementDetailsManagementDialog } from "./AgreementDetailsManagement";
import { DevelopersPermissions } from "../../../../../Permissions";

export const AgreementDetailsTable = ({
  translationPath,
  parentTranslationPath,
  data,
  filter,
  setFilter,
  reloadData,
  contactReletedWithDeveloperId,
  setIsHideFiled,
  isHideFiled,
}) => {
  const { t } = useTranslation(parentTranslationPath, "Shared");
  const [isLoading, setIsLoading] = useState({ info: false });
  const [activeItem, setActiveItem] = useState(null);
  const [isOpenEditDialog, setIsOpenEditDialog] = useState(false);
  const [isMissingInfoMSGOpen, setIsMissingInfoMSGOpen] = useState(false);

  const tableActionClicked = useCallback(
    (actionEnum, item, focusedRow, event) => {
      event.stopPropagation();
      event.preventDefault();
      setActiveItem(item);

      if (actionEnum === TableActions.delete.key) {
        setIsMissingInfoMSGOpen(true);
      } else if (actionEnum === TableActions.editText.key) {
        setIsOpenEditDialog(true);
      }
    },
    []
  );

  const onPageIndexChanged = (pageIndex) => {
    setFilter((item) => ({ ...item, pageIndex }));
  };
  const onPageSizeChanged = (pageSize) => {
    setFilter((item) => ({ ...item, pageIndex: 0, pageSize }));
  };

  const repeated = (list, filed) =>
    list &&
    list.map((item, index) => (
      <span key={index}>
        {item[filed]} {list.length - 1 != index && <span> , </span>}
      </span>
    ));

  const getActionTableWithPermissions = () => {
    const list = [];
    if (
      returnPropsByPermissions(
        DevelopersPermissions.EditAgreementDetails.permissionsId
      )
    )
      list.push({
        enum: TableActions.editText.key,
        title: t(`Shared:edit`),
      });
    list.push({
      enum: TableActions.delete.key,
      title: t(`Shared:delete`),
    });
    return list;
  };

  const GetAgreementDetailsByIdHandler = useCallback(async () => {
    setIsLoading((item) => ({ ...item, info: true }));

    const res = await GetAgreementDetailsByIdAPI(
      activeItem && activeItem.agreementDetailId
    );
    if (res && res.ErrorId) {
      showError(t(`failed-to-get-agreement-detail`));
    } else if (!(res && res.status && res.status !== 200)) {
    }
    setIsLoading((item) => ({ ...item, info: false }));
  }, [activeItem]);

  const DeleteHandler = useCallback(async () => {
    if (activeItem && activeItem.agreementDetailId) {
      setIsLoading((item) => ({ ...item, delete: true }));

      const res = await DeleteAgreementDetailsAPI(
        activeItem && activeItem.agreementDetailId
      );
      if (res && res.ErrorId) {
        showError(t(`failed-to-delete-agreement-detail`));
      } else if (!(res && res.status && res.status !== 200)) {
        showSuccess(t(`delete-agreement-detail-successfully`));
        setIsMissingInfoMSGOpen(false);
        setActiveItem(null);
        reloadData();
      }
      setIsLoading((item) => ({ ...item, delete: false }));
    }
  }, [activeItem]);

  useEffect(() => {
    if (activeItem) {
      GetAgreementDetailsByIdHandler(activeItem.agreementDetailId);
    }
  }, [activeItem]);

  return (
    <div className="d-flex-column mt-2">
      <Spinner isActive={isLoading.info || isLoading.delete} isAbsolute />
      <Tables
        data={data.result || []}
        headerData={[
          {
            id: 1,
            label: "agreement-type",
            input: "agreementType",
          },
          {
            id: 11,
            label: "agreement-status",
            input: "agreementStatus",
          },
          {
            id: 14,
            label: "agreement-validity-status",
            input: "agreementValidityStatus",
          },
          {
            id: 2,
            label: "agreement-start-date",
            input: "agreementStartDate",
            isDate: true,
          },
          {
            id: 3,
            label: "agreement-duration-months",
            input: "agreementDuration",
          },
          {
            id: 4,
            label: "agreement-end-date",
            input: "agreementEndDate",
            isDate: true,
            component: (item) =>
              item.agreementEndDate ? item.agreementEndDate : "Open",
          },
          {
            id: 12,
            label: "auto-renew-status",
            input: "isAutoRenew",
            component: (item) => (item.isAutoRenew ? "Enabled" : "Disabled"),
          },
          {
            id: 13,
            label: "auto-renew-period",
            input: "renewalPeriodInMonths",
            component: (item) => `${item.renewalPeriodInMonths} Months`,
          },
          {
            id: 5,
            label: "projects-names",
            input: "project-name",
            component: (item) =>
              repeated(item.agreementDetailsProjects, "propertyName"),
          },
          {
            id: 6,
            label: "signing-branches",
            input: "signing_branch",
            component: (item) =>
              repeated(item.agreementDetailsSigningBranches, "branchName"),
          },
          {
            id: 7,
            label: "authorizedes-signatories",
            input: "authorized_signatory",
            component: (item) =>
              repeated(item.agreementDetailsAuthorizedSignatories, "userName"),
          },
          {
            id: 9,
            label: "isMarketingBudgetAllocation",
            input: "isMarketingBudgetAllocation",
            component: (item, index) => {
              if (item.isMarketingBudgetAllocation === null) return '';
              const isYes = item.isMarketingBudgetAllocation;
              const className = isYes
                ? 'globel-open-style'
                : 'globel-closed-style';
              const label = isYes ? t('yes') : t('no');
              return (
                <div
                  className={className}
                  index={index}
                >
                  {label}
                </div>
              );
            },
          },
          {
            id: 10,
            label: "marketingBudgetAmount",
            input: "marketingBudgetAmount",
          },
          {
            id: 15,
            label: "createdBy",
            input: "createdBy",
          },
          {
            id: 16,
            label: "createdOn",
            input: "createdOn",
            component: (item) =>
              item.createdOn ? moment(item.createdOn).format("DD/MM/YYYY") : "",
          },
        ]}
        onPageIndexChanged={onPageIndexChanged}
        onPageSizeChanged={onPageSizeChanged}
        defaultActions={getActionTableWithPermissions()}
        actionsOptions={{
          onActionClicked: tableActionClicked,
        }}
        activePage={filter.pageIndex}
        parentTranslationPath={parentTranslationPath}
        translationPath={translationPath}
        itemsPerPage={filter.pageSize}
        totalItems={(data && data?.totalCount) || 0}
      />
      {isMissingInfoMSGOpen && activeItem && (
        <MessageConfirmDialog
          isDialogOpen={isMissingInfoMSGOpen}
          theme="warning"
          content={{
            heading: "delete-agreement-details",
            body:
              t(`are-you-sure-you-want-to-delete-delete-agreement-details-id`) +
              " " +
              ((activeItem && activeItem.agreementDetailId) || ""),
          }}
          confirmButton={{
            label: t(`confirm`),
            handler: () => DeleteHandler(),
          }}
          cancelButton={{
            label: t(`discard`),
            handler: () => {
              setIsMissingInfoMSGOpen(false);
            },
          }}
          parentTranslationPath={parentTranslationPath}
          translationPath={translationPath}
        />
      )}
      {isOpenEditDialog && activeItem && (
        <AgreementDetailsManagementDialog
          parentTranslationPath={parentTranslationPath}
          translationPath={translationPath}
          isOpen={isOpenEditDialog}
          onClose={() => {
            setIsOpenEditDialog(false);
            setActiveItem(null);
          }}
          onSave={(editItem) => {
            if (editItem) {
              setIsOpenEditDialog(false);
              setActiveItem(null);
              reloadData();
            }
          }}
          agreementDetailsValues={activeItem}
          contactReletedWithDeveloperId={contactReletedWithDeveloperId}
          isHideFiled={isHideFiled}
          setIsHideFiled={setIsHideFiled}
        />
      )}
    </div>
  );
};
